/**
 * Widget Selector Component
 *
 * Side panel for selecting and adding widgets to the dashboard.
 * Organized by category with search functionality.
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  X,
  Search,
  Plus,
  ClipboardList,
  PackageSearch,
  Truck,
  Warehouse,
  Send,
  TrendingUp,
  Activity,
  Users,
  Trophy,
  AlertCircle,
  ArrowLeftRight,
  Compass,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { useDashboard } from '../DashboardContext';
import { WIDGET_REGISTRY, getWidgetsByCategories } from '../widgetRegistry';
import { WidgetCategory, CATEGORY_INFO, WidgetDefinition } from '../types';
import { claudeQueryService } from '../../../../../services/claudeQueryService';

/**
 * Detect if text looks like a natural language query (not a widget search).
 * Must be >15 chars and contain question/domain keywords.
 */
function isNaturalLanguageQuery(text: string): boolean {
  if (text.length < 16) return false;
  const lower = text.toLowerCase();
  const questionWords = [
    'vis', 'hent', 'sammenlign', 'finn', 'hva', 'hvor', 'hvilke',
    'tell', 'beregn', 'topp', 'show', 'get', 'compare', 'find',
    'list', 'gi meg', 'lag', 'generer',
  ];
  const domainWords = [
    'plukk', 'mottak', 'ordre', 'beholdning', 'operatør', 'vare',
    'lager', 'linje', 'forsendelse', 'bin', 'sone', 'zone',
    'picking', 'receipt', 'order', 'inventory', 'item', 'warehouse',
    'uke', 'måned', 'dag', 'i dag', 'denne', 'forrige', 'siste',
  ];
  const hasQuestion = questionWords.some((w) => lower.includes(w));
  const hasDomain = domainWords.some((w) => lower.includes(w));
  return hasQuestion || hasDomain;
}

interface WidgetSelectorProps {
  isOpen: boolean;
  onClose: () => void;
}

// Map categories to icons
const CATEGORY_ICONS: Record<WidgetCategory, React.ReactNode> = {
  compass: <Compass className="w-4 h-4" />,
  orders: <ClipboardList className="w-4 h-4" />,
  picking: <PackageSearch className="w-4 h-4" />,
  receiving: <Truck className="w-4 h-4" />,
  inventory: <Warehouse className="w-4 h-4" />,
  shipping: <Send className="w-4 h-4" />,
  kpi: <TrendingUp className="w-4 h-4" />,
  activity: <Activity className="w-4 h-4" />,
  operators: <Users className="w-4 h-4" />,
  gamification: <Trophy className="w-4 h-4" />,
  alerts: <AlertCircle className="w-4 h-4" />,
  comparison: <ArrowLeftRight className="w-4 h-4" />,
};

const WidgetSelector: React.FC<WidgetSelectorProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { addWidget, state } = useDashboard();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<WidgetCategory>>(
    new Set<WidgetCategory>(['compass', 'orders', 'picking'])
  );
  const [isCreatingWidget, setIsCreatingWidget] = useState(false);
  const [quickWidgetError, setQuickWidgetError] = useState<string | null>(null);

  // Get widgets organized by category
  const widgetsByCategory = useMemo(() => getWidgetsByCategories(), []);

  // Filter widgets by search query
  const filteredWidgets = useMemo(() => {
    if (!searchQuery.trim()) return widgetsByCategory;

    const query = searchQuery.toLowerCase();
    const filtered = new Map<WidgetCategory, WidgetDefinition[]>();

    widgetsByCategory.forEach((widgets, category) => {
      const matching = widgets.filter(
        (w) =>
          w.name.toLowerCase().includes(query) ||
          w.description.toLowerCase().includes(query)
      );
      if (matching.length > 0) {
        filtered.set(category, matching);
      }
    });

    return filtered;
  }, [widgetsByCategory, searchQuery]);

  // Check if widget is already in dashboard (allowMultiple widgets can always be added)
  const isWidgetAdded = (widgetId: string) => {
    const def = WIDGET_REGISTRY.find((w) => w.id === widgetId);
    if (def?.allowMultiple) return false;
    return state.currentLayout.widgets.some((w) => w.widgetId === widgetId);
  };

  // Toggle category expansion
  const toggleCategory = (category: WidgetCategory) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Detect if search is a natural language query
  const isNLQuery = useMemo(() => isNaturalLanguageQuery(searchQuery), [searchQuery]);

  // Handle adding widget
  const handleAddWidget = (widgetId: string) => {
    addWidget(widgetId);
  };

  // Handle quick widget creation from NL query
  const handleQuickWidget = useCallback(async () => {
    if (!searchQuery.trim() || isCreatingWidget) return;
    setIsCreatingWidget(true);
    setQuickWidgetError(null);

    try {
      const result = await claudeQueryService.createQuickWidget(searchQuery.trim());
      if (result.success && result.reportId) {
        addWidget('compass-report', undefined, {
          reportId: result.reportId,
          displayMode: result.displayMode || 'table',
        });
        onClose();
      } else {
        setQuickWidgetError(result.errorMessage || t('manager.modules.wmsDashboard.widgetSelector.createWidgetError', {}, 'Kunne ikke opprette widget'));
      }
    } catch (err: any) {
      setQuickWidgetError(err.message || t('manager.modules.wmsDashboard.widgetSelector.genericError', {}, 'En feil oppstod'));
    } finally {
      setIsCreatingWidget(false);
    }
  }, [searchQuery, isCreatingWidget, addWidget, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-96 bg-gray-900 border-l border-gray-700 z-50 flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">{t('manager.modules.wmsDashboard.toolbar.addWidget', {}, 'Legg til widget')}</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('manager.modules.wmsDashboard.widgetSelector.searchPlaceholder', {}, 'Søk widgets eller skriv et spørsmål...')}
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setQuickWidgetError(null); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isNLQuery && !isCreatingWidget) {
                  e.preventDefault();
                  handleQuickWidget();
                }
              }}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>
        </div>

        {/* Quick Widget from NL query */}
        {isNLQuery && (
          <div className="px-4 pt-3">
            <div className="p-3 rounded-lg border border-blue-500/50 bg-blue-500/10">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-blue-300">
                    {t('manager.modules.wmsDashboard.widgetSelector.createCompassWidget', {}, 'Opprett Compass-widget')}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 truncate">
                    &laquo;{searchQuery}&raquo;
                  </p>
                  {quickWidgetError && (
                    <p className="text-xs text-red-400 mt-1">{quickWidgetError}</p>
                  )}
                </div>
                <button
                  onClick={handleQuickWidget}
                  disabled={isCreatingWidget}
                  className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  {isCreatingWidget ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      {t('manager.modules.wmsDashboard.widgetSelector.creating', {}, 'Oppretter...')}
                    </>
                  ) : (
                    <>
                      <Compass className="w-3.5 h-3.5" />
                      {t('common.buttons.create', {}, 'Opprett')}
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {t('manager.modules.wmsDashboard.widgetSelector.quickWidgetHint', {}, 'Trykk Enter eller klikk Opprett for å generere widget automatisk')}
              </p>
            </div>
          </div>
        )}

        {/* Widget list */}
        <div className="flex-1 overflow-y-auto p-4">
          {Array.from(filteredWidgets.entries()).map(([category, widgets]) => {
            const categoryInfo = CATEGORY_INFO[category];
            const isExpanded = expandedCategories.has(category);

            return (
              <div key={category} className="mb-4">
                {/* Category header */}
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <span className="text-gray-400">{CATEGORY_ICONS[category]}</span>
                  <span className="text-sm font-medium text-white flex-1 text-left">
                    {t(`manager.modules.wmsDashboard.categories.${category}`, {}, categoryInfo.label)}
                  </span>
                  <span className="text-xs text-gray-500">{widgets.length}</span>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Widget items */}
                {isExpanded && (
                  <div className="mt-2 space-y-2 ml-2">
                    {widgets.map((widget) => {
                      const isAdded = isWidgetAdded(widget.id);

                      return (
                        <div
                          key={widget.id}
                          className={`
                            p-3 rounded-lg border transition-all
                            ${isAdded
                              ? 'bg-gray-800/50 border-gray-700 opacity-60'
                              : 'bg-gray-800 border-gray-700 hover:border-blue-500/50 hover:bg-gray-750'
                            }
                          `}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-medium text-white truncate flex items-center gap-1.5">
                                {t(`manager.modules.wmsDashboard.widgets.${widget.id}.name`, {}, widget.name)}
                                {widget.hasBackend && (
                                  <span className="relative flex h-2 w-2 flex-shrink-0" title={t('manager.modules.wmsDashboard.widgetSelector.connectedLiveData', {}, 'Koblet til live data')}>
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                                  </span>
                                )}
                              </h3>
                              <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">
                                {t(`manager.modules.wmsDashboard.widgets.${widget.id}.description`, {}, widget.description)}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <span className="text-xs text-gray-500 px-2 py-0.5 bg-gray-700/50 rounded">
                                  {widget.minWidth}x{widget.minHeight}
                                </span>
                                {widget.defaultRefreshInterval > 0 && (
                                  <span className="text-xs text-gray-500 px-2 py-0.5 bg-gray-700/50 rounded">
                                    {widget.defaultRefreshInterval}s
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleAddWidget(widget.id)}
                              disabled={isAdded}
                              className={`
                                flex-shrink-0 p-2 rounded-lg transition-colors
                                ${isAdded
                                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                  : 'bg-blue-600 hover:bg-blue-500 text-white'
                                }
                              `}
                              title={isAdded ? t('manager.modules.wmsDashboard.widgetSelector.alreadyAdded', {}, 'Allerede lagt til') : t('manager.modules.wmsDashboard.toolbar.addWidget', {}, 'Legg til widget')}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {filteredWidgets.size === 0 && (
            <div className="text-center text-gray-500 py-8">
              <p>{t('manager.modules.wmsDashboard.widgetSelector.noWidgetsFound', {}, 'Ingen widgets funnet')}</p>
              <p className="text-sm mt-1">{t('manager.modules.wmsDashboard.widgetSelector.tryAnotherSearch', {}, 'Prøv et annet søkeord')}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 bg-gray-800/50">
          <div className="flex items-center justify-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <p className="text-xs text-gray-500">
              {t('manager.modules.wmsDashboard.widgetSelector.liveDataAvailable', {}, 'Live data tilgjengelig')}
            </p>
          </div>
          <p className="text-xs text-gray-500 text-center mt-1">
            {t('manager.modules.wmsDashboard.widgetSelector.widgetsAvailable', { '0': WIDGET_REGISTRY.length }, `${WIDGET_REGISTRY.length} widgets tilgjengelig`)}
          </p>
        </div>
      </div>
    </>
  );
};

export default WidgetSelector;
